import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import ToastContainer from '../../components/ToastContainer';
import { apiFetch } from '../../utils/apiClient';
import { formatDate } from '../../utils/format';
import { useVendor } from '../../contexts/VendorContext';

interface VendorRow {
  id: number;
  name: string;
  business_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  city: string | null;
  notes: string | null;
  is_active: boolean;
  approved_at: string | null;
  preferred_language: string;
  commission_rate: string | null;
  shopify_vendor_name: string | null;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  last_login: string | null;
  created_at: string;
  inventory_items_count: number;
  onboarding_missing: string[];
  onboarding_complete: boolean;
}

type Tab = 'pending' | 'active' | 'inactive';

interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

const inputClass = 'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[40px] focus:outline-none focus:ring-2 focus:ring-blue-500';

const AdminVendors: React.FC = () => {
  const { t } = useTranslation();
  const { settings } = useVendor();
  const emailEnabled = !!settings.email_enabled;
  const [tab, setTab] = useState<Tab>('pending');
  const [credential, setCredential] = useState<{ username: string; password: string; store: string } | null>(null);
  const [summary, setSummary] = useState({ pending: 0, active: 0, inactive: 0 });
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = (message: string, type: ToastData['type']) =>
    setToasts((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, message, type }]);

  const loadSummary = useCallback(async () => {
    const r = await apiFetch('/api/admin/vendors/summary/');
    if (r.ok) setSummary(await r.json());
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: tab, page_size: '100' });
      if (search.trim()) params.set('search', search.trim());
      const r = await apiFetch(`/api/admin/vendors/?${params.toString()}`);
      const data = await r.json();
      setVendors(data.results || []);
    } catch {
      addToast(t('common.genericError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [tab, search, t]);

  useEffect(() => {
    load();
    loadSummary();
  }, [load, loadSummary]);

  const action = async (vendor: VendorRow, path: string, successMsg: string, body?: object) => {
    setBusyId(vendor.id);
    try {
      const r = await apiFetch(`/api/admin/vendors/${vendor.id}/${path}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || data.detail || t('common.genericError'));
      addToast(successMsg, 'success');
      await load();
      await loadSummary();
    } catch (err: any) {
      addToast(err.message || t('common.genericError'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const setTemporaryPassword = async (vendor: VendorRow) => {
    if (!window.confirm(t('admin.v.tempPasswordConfirm', { name: vendor.name }))) return;
    setBusyId(vendor.id);
    try {
      const r = await apiFetch(`/api/admin/vendors/${vendor.id}/set-temporary-password/`, { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || t('common.genericError'));
      setCredential({ username: data.username, password: data.temporary_password, store: vendor.name });
    } catch (err: any) {
      addToast(err.message || t('common.genericError'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'pending', label: t('admin.v.pending'), count: summary.pending },
    { key: 'active', label: t('admin.v.active'), count: summary.active },
    { key: 'inactive', label: t('admin.v.inactive'), count: summary.inactive },
  ];

  return (
    <Layout>
      <ToastContainer toasts={toasts} onRemove={(id) => setToasts((p) => p.filter((x) => x.id !== id))} />
      {credential && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">{t('admin.v.credentialTitle', { store: credential.store })}</h2>
            <p className="text-sm text-gray-600">{t('admin.v.credentialBody')}</p>
            <dl className="rounded-md bg-gray-50 border border-gray-200 p-3 text-sm space-y-1">
              <div className="flex justify-between gap-3"><dt className="text-gray-500">{t('signup.username')}</dt><dd className="font-mono font-medium select-all">{credential.username}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-gray-500">{t('auth.password')}</dt><dd className="font-mono font-medium select-all">{credential.password}</dd></div>
            </dl>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { navigator.clipboard?.writeText(`${t('signup.username')}: ${credential.username}\n${t('auth.password')}: ${credential.password}`).catch(() => undefined); }}
                className="px-4 py-2 rounded-md border border-gray-300 text-sm min-h-[40px]"
              >
                {t('admin.v.copy')}
              </button>
              <button onClick={() => setCredential(null)} className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium min-h-[40px]">
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="w-full max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('admin.v.title')}</h1>
          <Link to="/admin/vendors/new" className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium min-h-[44px]">
            + {t('admin.onb.onboardButton')}
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <div className="flex gap-2 overflow-x-auto">
            {tabs.map((tb) => (
              <button
                key={tb.key}
                onClick={() => setTab(tb.key)}
                className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap min-h-[40px] ${tab === tb.key ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}
              >
                {tb.label} <span className="opacity-80">({tb.count})</span>
              </button>
            ))}
          </div>
          <input type="search" placeholder={t('admin.v.searchPlaceholder')} className={`${inputClass} sm:max-w-xs`} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
        ) : vendors.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center text-gray-600">{t('admin.v.noVendors')}</div>
        ) : (
          <ul className="space-y-3">
            {vendors.map((v) => {
              return (
                <li key={v.id} className="bg-white shadow rounded-lg">
                  <div className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">{v.name}{v.business_name && v.business_name !== v.name ? ` · ${v.business_name}` : ''}</p>
                      <p className="text-sm text-gray-600">
                        {[v.first_name, v.last_name].filter(Boolean).join(' ') || v.username} · {v.email || v.contact_email} · {v.contact_phone || '—'} · {v.city || '—'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('admin.v.joined')} {formatDate(v.created_at)} · {t('admin.v.lastLogin')}: {v.last_login ? formatDate(v.last_login) : t('admin.v.never')} · {t('admin.v.items')}: {v.inventory_items_count}
                        {v.approved_at && v.is_active && <> · {t('admin.v.approvedOn', { date: formatDate(v.approved_at) })}</>}
                      </p>
                      {!v.is_active && !v.approved_at && (
                        <p className="mt-1.5">
                          {v.onboarding_complete ? (
                            <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 px-2.5 py-0.5 text-xs font-semibold">{t('admin.onb.readyToApprove')}</span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2.5 py-0.5 text-xs font-semibold" title={v.onboarding_missing.join(', ')}>
                              {t('admin.onb.incomplete', { count: v.onboarding_missing.length })}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 flex-shrink-0">
                      {!v.is_active && (
                        <button
                          disabled={busyId === v.id || !v.onboarding_complete}
                          title={!v.onboarding_complete ? t('admin.onb.approveBlocked') : undefined}
                          onClick={() => action(v, 'approve', t('admin.v.approved', { name: v.name }))}
                          className="px-3 py-2 rounded-md bg-green-600 text-white text-sm font-medium min-h-[40px] disabled:opacity-50"
                        >
                          {t('admin.v.approve')}
                        </button>
                      )}
                      {v.is_active && (
                        <button disabled={busyId === v.id} onClick={() => action(v, 'deactivate', t('admin.v.deactivated', { name: v.name }))} className="px-3 py-2 rounded-md border border-red-300 text-red-700 text-sm font-medium min-h-[40px] disabled:opacity-50">
                          {t('admin.v.deactivate')}
                        </button>
                      )}
                      {emailEnabled ? (
                        <button disabled={busyId === v.id} onClick={() => action(v, 'send-password-reset', t('admin.v.resetSent'))} className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 text-sm min-h-[40px] disabled:opacity-50">
                          {t('admin.v.resetPassword')}
                        </button>
                      ) : (
                        <button disabled={busyId === v.id} onClick={() => setTemporaryPassword(v)} className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 text-sm min-h-[40px] disabled:opacity-50">
                          {t('admin.v.tempPassword')}
                        </button>
                      )}
                      <Link to={`/admin/vendors/${v.id}`} className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 text-gray-700 text-sm min-h-[40px]">
                        {v.is_active ? t('admin.v.details') : t('admin.onb.onboarding')}
                      </Link>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Layout>
  );
};

export default AdminVendors;
